import { z } from 'zod';
import {
  integrationProviders,
  webhookEventStatuses,
  type IntegrationProvider,
  type WebhookEventStatus,
} from '../enums.js';
import { trimmed } from './_fields.js';
import { docaliaSettingsSchema, type DocaliaSettings } from './docalia.js';
import {
  doctenaSettingsInputSchema,
  type DoctenaPracticeDto,
  type DoctenaSettings,
} from './doctena.js';

/**
 * Chaque plateforme d'agenda a son propre contrat — réglages, associations, boîtes de réception,
 * santé des flux —, réexporté ici pour que `@osteopro/shared` reste le seul point d'entrée du
 * domaine. Ce que les deux plateformes d'agenda partagent réellement — boîtes de réception, santé
 * des flux — vit dans `integrations-sync.ts` ; le reste (connexions, statuts de webhook) ici.
 */
export * from './integrations-sync.js';
export * from './doctena.js';
export * from './doctena-members.js';
export * from './docalia.js';

/**
 * Intégrations : un seul contrat pour les trois fournisseurs (Buckaroo, Doctena, Docalia).
 *
 * Le DTO de connexion dit seulement s'il existe une clé (`hasCredentials`). Elle ne part pas
 * avec la liste : le propriétaire la redemande d'un bouton (`RevealedApiKeyDto`). Le secret
 * des notifications Buckaroo, tiré par le serveur, ne sort jamais.
 */

export const integrationProviderParamSchema = z
  .object({ provider: z.enum(integrationProviders) })
  .strict();
export type IntegrationProviderParam = z.infer<typeof integrationProviderParamSchema>;

/* -------------------------------------------------------------------------- */
/* Identifiants                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Les trois fournisseurs s'authentifient par une clé d'API porteuse. Elle est chiffrée avant
 * d'atteindre la base (`integration_connections.credentials_enc`) et n'est jamais journalisée.
 *
 * Doctena exige une seconde moitié : l'email du compte, envoyé dans l'en-tête `email` à côté de
 * l'en-tête `token`. C'est donc **un identifiant**, pas un réglage — il est chiffré avec la clé
 * et ne ressort jamais dans un objet de transfert, quand bien même l'écran de la V1 l'affichait.
 *
 * Le secret des rappels signés de Buckaroo n'est **pas** ici : c'est le serveur qui le tire et le
 * range à côté de la clé, jamais le navigateur qui le fournit.
 */
export const integrationCredentialsSchema = z
  .object({ apiKey: trimmed(512), email: z.email().max(320).optional() })
  .strict();
export type IntegrationCredentials = z.infer<typeof integrationCredentialsSchema>;

/* -------------------------------------------------------------------------- */
/* Réglages, par fournisseur                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Identifiant de point de vente Buckaroo : `s_` suivi de lettres et de chiffres (`s_1234abcd` dans
 * la documentation). La longueur n'est pas documentée : on borne large plutôt que de deviner.
 */
export const BUCKAROO_STORE_ID_PATTERN = /^s_[A-Za-z0-9]{4,64}$/;

/**
 * Préfixe de la référence que porte un QR de facture Wero d'OsteoPro : `OP-{numéro de facture}`.
 *
 * Un même compte Buckaroo peut servir à plusieurs logiciels — PhysioPro et OsteoPro chez le même
 * praticien, sur le même point de vente. Le rapprochement ne retient donc que les ventes dont la
 * référence commence par ce préfixe, le retire pour retrouver la facture, et ignore les autres.
 * **Seule source** du préfixe : le constructeur d'adresse du QR et l'adaptateur des ventes passent
 * par les deux fonctions ci-dessous.
 */
export const WERO_REFERENCE_PREFIX = 'OP-';

export function weroReferenceOf(invoiceNumber: string): string {
  return `${WERO_REFERENCE_PREFIX}${invoiceNumber}`;
}

/** Le numéro de facture d'une référence d'OsteoPro, ou `null` pour une référence étrangère. */
export function invoiceNumberOfWeroReference(reference: string): string | null {
  if (!reference.startsWith(WERO_REFERENCE_PREFIX)) return null;
  const invoiceNumber = reference.slice(WERO_REFERENCE_PREFIX.length).trim();
  return invoiceNumber === '' ? null : invoiceNumber;
}

/**
 * Buckaroo : le seul réglage est le **point de vente** (`storeId`). Ce n'est pas un secret — il
 * est imprimé dans chaque QR de facture — et c'est lui qui permet au rappel signé de retrouver le
 * cabinet.
 *
 * **Les hôtes de Buckaroo ne sont pas ici.** La V1 laissait le cabinet configurer l'environnement
 * de Payconiq, donc l'hôte appelé par le serveur. Ceux de Buckaroo sont des constantes du client.
 */
export const buckarooSettingsSchema = z
  .object({
    storeId: z
      .string()
      .trim()
      .regex(BUCKAROO_STORE_ID_PATTERN, 'Identifiant de point de vente Buckaroo attendu (s_…)'),
  })
  .strict();
export type BuckarooSettings = z.infer<typeof buckarooSettingsSchema>;

export interface IntegrationSettingsByProvider {
  buckaroo: BuckarooSettings;
  doctena: DoctenaSettings;
  docalia: DocaliaSettings;
}

export type IntegrationSettings = IntegrationSettingsByProvider[IntegrationProvider];

/** Le schéma à appliquer aux réglages reçus, choisi par le fournisseur de l'URL. */
export const integrationSettingsSchemas: Readonly<{
  [P in IntegrationProvider]: z.ZodType<IntegrationSettingsByProvider[P]>;
}> = {
  buckaroo: buckarooSettingsSchema,
  doctena: doctenaSettingsInputSchema,
  docalia: docaliaSettingsSchema,
};

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Enregistrement d'une connexion. `credentials` absent veut dire « inchangées » : l'écran de
 * paramètres modifie l'identifiant marchand sans redemander la clé d'API, qu'il ne connaît pas.
 * `settings` est validé une seconde fois par le schéma du fournisseur, côté service.
 */
export const integrationUpsertSchema = z
  .object({
    credentials: integrationCredentialsSchema.optional(),
    settings: z.unknown(),
    isEnabled: z.boolean(),
  })
  .strict();
export type IntegrationUpsertInput = z.infer<typeof integrationUpsertSchema>;

/**
 * **Éprouver des identifiants avant de les enregistrer**, comme le faisait l'écran de la V1.
 *
 * Le corps est **facultatif** : absent, ce sont les identifiants déjà enregistrés qu'on teste,
 * ce qui était le seul comportement jusqu'ici. Présent, ce sont ceux-là — et **rien n'est écrit
 * ni chiffré en base** : un praticien doit pouvoir vérifier une clé qu'il vient de recevoir sans
 * écraser celle qui fonctionne.
 *
 * `settings` est revalidé par le schéma du fournisseur côté service, comme à l'enregistrement :
 * un jeton de production éprouvé contre le bac à sable ne prouverait rien.
 */
export const integrationTestSchema = z
  .object({
    credentials: integrationCredentialsSchema,
    settings: z.unknown(),
  })
  .strict();
export type IntegrationTestInput = z.infer<typeof integrationTestSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface IntegrationConnectionDto {
  provider: IntegrationProvider;
  isEnabled: boolean;
  /** Vrai dès qu'une clé chiffrée existe. La clé elle-même n'est pas dans cet objet. */
  hasCredentials: boolean;
  /**
   * L'email du compte Doctena, quand il y en a un. **Ce n'est pas un secret** : c'est
   * l'identifiant de compte du cabinet chez le fournisseur, et l'écran doit pouvoir dire
   * « configuration existante pour untel@cabinet.lu » comme le faisait la V1. La clé, elle, ne
   * sort jamais — c'est elle que vise l'avertissement des identifiants plus haut.
   *
   * Nul pour les fournisseurs qui n'ont pas d'email, et pour une connexion qui n'en porte pas.
   */
  accountEmail: string | null;
  /**
   * Buckaroo : le rapprochement automatique est activé — une notification signée est déclarée
   * chez Buckaroo, par le geste explicite « Activer », jamais par le seul enregistrement d'une clé.
   * Faux pour les autres fournisseurs. Dit ce qui est déclaré, pas ce que l'offre permet : un
   * cabinet redescendu sous Professionnel garde sa déclaration, que le serveur ignore alors.
   */
  autoReconciliation: boolean;
  settings: IntegrationSettings | null;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Clé d'API déchiffrée, renvoyée seulement au propriétaire qui demande à la voir.
 * Pas d'email (déjà sur la connexion), pas de secret de notification.
 */
export interface RevealedApiKeyDto {
  apiKey: string;
}

export interface IntegrationListDto {
  /** Les trois fournisseurs, toujours, même sans connexion enregistrée. */
  connections: IntegrationConnectionDto[];
}

/** Un calendrier Doctena, tel que le test le rapporte. Aucune donnée de patient. */
export interface IntegrationCalendarDto {
  calendarId: string;
  calendarName: string;
  practiceId: string;
  practiceName: string;
}

export interface IntegrationTestResultDto {
  provider: IntegrationProvider;
  ok: boolean;
  testedAt: string;
  /**
   * Les calendriers trouvés, pour Doctena et lorsque le test réussit. C'est ce que la V1
   * affichait pour **prouver** la connexion : un « réussi » sans rien derrière ne dit pas qu'on
   * parle au bon compte. Liste vide pour les autres fournisseurs, et en cas d'échec.
   *
   * Doctena liste les calendriers **par cabinet** : ceux-ci sont ceux du cabinet des réglages
   * éprouvés ou, à défaut, du seul cabinet que le compte voit. Plusieurs cabinets et aucun
   * choisi : la liste reste vide, et `practices` dit entre quoi choisir.
   */
  calendars: IntegrationCalendarDto[];
  /**
   * Les cabinets Doctena que le compte voit, lorsque le test réussit. C'est parmi eux que
   * l'écran fait choisir celui qu'on synchronise — il ne se saisit jamais à la main. Vide pour
   * les autres fournisseurs, et en cas d'échec.
   */
  practices: DoctenaPracticeDto[];
}

export interface WebhookAckDto {
  status: WebhookEventStatus;
}

export const webhookEventStatusSchema = z.enum(webhookEventStatuses);
